import { QuestionCircleOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import { SelectTokenizeType, StakingNftType, TokenizeCollectionType } from 'Modules'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { TIER_ITEMS } from 'constants/pto'
import { stakingNFTDurationOptions, StakingNFTPoolType } from 'constants/staking-nft'
import { useStakeNFTMeyReward } from 'hooks/stake-nft/use-stake-nft-mey-reward'
import { useStakeNFTUsdcReward } from 'hooks/stake-nft/use-stake-nft-usdc-reward'
import { logInfo } from 'utils/logger'

import SelectTokenize from './select-tokenize'

const ModalAddNftCollection = ({
  isModalOpen,
  setIsModalOpen,
  messageApi,
  stakingTokenize,
  onSubmit,
  stakingNft,
  nftCollection,
}: {
  isModalOpen: boolean
  messageApi: any
  stakingTokenize: string
  stakingNft: StakingNftType | null
  setIsModalOpen: (open: boolean) => void
  onSubmit?: () => Promise<void>
  nftCollection?: TokenizeCollectionType | null
}) => {
  const [form] = Form.useForm()
  const { address } = useAccount()

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [hasNftCollection, setHasNftCollection] = useState<boolean>(false)
  const [token, setSelectedToken] = useState<SelectTokenizeType | null>(null)

  const isMeyRewardPool = stakingNft?.name === StakingNFTPoolType.MEY
  const isUsdcRewardPool = stakingNft?.name === StakingNFTPoolType.USDC

  const {
    isContractManager: isStakeNftMeyManager,
    addNftToStakeMeyPool,
    updateNftToStakeMeyPool,
  } = useStakeNFTMeyReward(stakingNft?.contractAddress)
  const {
    isContractManager: isStakeNftUsdcManager,
    addNftToStakeUsdcPool,
    updateNftToStakeUsdcPool,
  } = useStakeNFTUsdcReward(stakingNft?.contractAddress)

  const initialData = useMemo(() => {
    return {
      stakingTokenize: stakingTokenize,
      aprBaseBP: 0,
      aprBonusTiers: TIER_ITEMS.filter((tier) => !!tier.allowNftCollection).map((tier) => ({
        ...tier,
        rewardBonus: 0,
      })),
      isActive: true,
    }
  }, [stakingTokenize])

  const handleTokenSelected = useCallback(
    async (token: SelectTokenizeType) => {
      setSelectedToken(token)
      if (token) {
        const nftCollection = await AppAdmin.getDetailNftCollection({
          stakingTokenize: stakingTokenize,
          tokenize: token._id,
        })
        if (nftCollection) {
          setHasNftCollection(true)
          const aprBonusTiers = TIER_ITEMS.filter((tier) => tier.allowNftCollection).map(
            (tier) => ({
              ...tier,
              aprBonusBP:
                nftCollection.aprBonusTiers.find((t) => t.tierId === tier.tierId)?.aprBonusBP ?? 0,
            })
          )

          form.setFieldsValue({
            aprBaseBP: nftCollection.aprBaseBP,
            aprBonusTiers: aprBonusTiers,
            isActive: nftCollection.isActive,
            duration: nftCollection.properties.duration,
            rewardBase: nftCollection.properties.tokenPerNFT,
            endDate: dayjs(Number(nftCollection.properties.endDate * 1000)),
            startDate: dayjs(Number(nftCollection.properties.startDate * 1000)),
          })
        } else {
          setHasNftCollection(false)
          form.setFieldsValue({
            aprBaseBP: 0,
            aprBonusTiers: initialData.aprBonusTiers,
          })
        }
      }
    },
    [form, initialData.aprBonusTiers, stakingTokenize]
  )

  useEffect(() => {
    if (isModalOpen) {
      if (nftCollection?.tokenize) {
        form.setFieldsValue({
          tokenize: nftCollection.tokenize._id,
        })
        handleTokenSelected(nftCollection.tokenize as SelectTokenizeType)
      } else {
        setSelectedToken(null)
        setHasNftCollection(false)
        form.setFieldsValue(initialData)
      }
    }
  }, [form, handleTokenSelected, initialData, isModalOpen, nftCollection])

  const handleOk = async () => {
    try {
      setIsSubmitting(true)
      const values = await form.validateFields()
      values.startDate = values.startDate / 1000 // to seconds
      values.endDate = values.endDate / 1000 // to seconds

      if (
        (!isStakeNftMeyManager && isMeyRewardPool) ||
        (!isStakeNftUsdcManager && isUsdcRewardPool) ||
        !address
      ) {
        messageApi.open({
          type: 'error',
          content: 'Your wallet address does not have permission to add NFT Collection!',
        })
        return
      }

      if (!token || !token.contractAddress || !stakingNft?.contractAddress) {
        messageApi.open({
          type: 'error',
          content: 'Please select tokenize!',
        })
        return
      }

      let createdPoolResult = undefined

      // add NFT Stake to Mey Pool
      if (isMeyRewardPool) {
        if (hasNftCollection) {
          createdPoolResult = await updateNftToStakeMeyPool({
            fields: values,
            ptoId: token._id,
            collectionAddress: token.contractAddress,
          })
        } else {
          createdPoolResult = await addNftToStakeMeyPool({
            fields: values,
            ptoId: token._id,
            collectionAddress: token.contractAddress,
          })
        }
      }

      // add NFT Stake to USDC Pool
      if (isUsdcRewardPool) {
        if (hasNftCollection) {
          createdPoolResult = await updateNftToStakeUsdcPool({
            fields: values,
            ptoId: token._id,
            collectionAddress: token.contractAddress,
          })
        } else {
          createdPoolResult = await addNftToStakeUsdcPool({
            fields: values,
            ptoId: token._id,
            collectionAddress: token.contractAddress,
          })
        }
      }

      if (createdPoolResult) {
        const res = await AppAdmin.addNftCollection({
          tokenize: values.tokenize,
          aprBaseBP: values.aprBaseBP || 0,
          stakingTokenize: stakingTokenize,
          aprBonusTiers: values.aprBonusTiers.map((t: any) => ({
            tierId: t.tierId,
            aprBonusBP: t.aprBonusBP || 0,
          })),
          isActive: values.isActive,
          ...(isMeyRewardPool && {
            properties: {
              startDate: Number(values.startDate),
              endDate: Number(values.endDate),
              duration: values.duration,
              tokenPerNFT: values.rewardBase,
            },
          }),
        })

        if (res?.data.success === true) {
          messageApi.open({
            type: 'success',
            content: `${hasNftCollection ? 'Update' : 'Add'} NFT Collection successfully`,
          })
          setIsModalOpen(false)

          if (onSubmit) await onSubmit()
          form.resetFields()
        }

        return
      }
    } catch (error) {
      logInfo(error)
      message.error(String(error))
    } finally {
      setIsSubmitting(false)
      setIsModalOpen(false)
      setSelectedToken(null)
      form.resetFields()
    }
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedToken(null)
    form.resetFields()
  }

  const isTokenSelected = form.getFieldValue('tokenize') !== null

  return (
    <Modal
      title={nftCollection ? 'Edit NFT Collection' : 'Add NFT Collection to Pool'}
      open={isModalOpen}
      className="modalAddOrUpdateNftCollection"
      destroyOnClose={true}
      width={500}
      centered
      onCancel={handleCancel}
      footer={null}
    >
      <Form form={form} layout="vertical" initialValues={initialData} onFinish={handleOk}>
        <Form.Item
          hidden={true}
          label={
            <span>
              <Popover
                content={
                  <>
                    <p>The ID of the pool is used to identify the pool in the database.</p>
                    <p>Pool Id cannot be changed</p>
                  </>
                }
                placement="top"
                title="Explain"
              >
                <span style={{ marginLeft: 5 }}>
                  <QuestionCircleOutlined />
                </span>
              </Popover>
            </span>
          }
          name="id"
        >
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="Select PTO"
          name="tokenize"
          rules={[{ required: true, message: 'Please select a tokenize!' }]}
        >
          <SelectTokenize
            onTokenSelected={handleTokenSelected}
            disabled={!!nftCollection}
            stakingTokenize={stakingTokenize}
            tokenize={nftCollection?.tokenize?._id || null}
          />
        </Form.Item>

        {stakingNft?.name === StakingNFTPoolType.USDC && (
          <Form.Item
            label="APR (%)"
            name="aprBaseBP"
            rules={[{ required: true, message: 'Please input APR Base BP!' }]}
          >
            <InputNumber
              className="w-full"
              min={0}
              placeholder="Enter APR Base BP"
              disabled={!isTokenSelected}
            />
          </Form.Item>
        )}

        {stakingNft?.name === StakingNFTPoolType.MEY && (
          <>
            <Form.Item
              label="Token Amount Per NFT/Year"
              name="rewardBase"
              rules={[{ required: true, message: 'Please input Reward Per NFT Per Year!' }]}
            >
              <InputNumber
                className="w-full"
                min={0.01}
                placeholder="Enter Reward Per NFT Per Year"
              />
            </Form.Item>
            <Form.Item
              name="startDate"
              label={
                <>
                  Start Date
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">The start date of the staking period</p>
                      </div>
                    }
                    placement="right"
                  >
                    <QuestionCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </>
              }
              rules={[{ required: true, message: 'Staking Start Date & Time is required' }]}
            >
              <DatePicker
                showTime
                disabled={!isTokenSelected}
                format="DD/MM/YYYY HH:mm:ss"
                placeholder="dd/mm/yyyy --:-- --"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="endDate"
              label={
                <>
                  End Date
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">The end date of the staking period</p>
                      </div>
                    }
                    placement="right"
                  >
                    <QuestionCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </>
              }
              rules={[{ required: true, message: 'Staking End Date & Time is required' }]}
            >
              <DatePicker
                showTime
                disabled={!isTokenSelected}
                format="DD/MM/YYYY HH:mm:ss"
                placeholder="dd/mm/yyyy --:-- --"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              label="Duration"
              name="duration"
              rules={[{ required: true, message: 'Please select a staking duration!' }]}
            >
              <Select placeholder="Enter Staking Duration" options={stakingNFTDurationOptions} />
            </Form.Item>
          </>
        )}

        <Divider>APR Bonus Tiers</Divider>

        <Form.List name="aprBonusTiers">
          {(fields) => (
            <Row gutter={[16, 16]}>
              {fields.map(({ key, name, ...restField }) => {
                const tierInfo = TIER_ITEMS[name]
                return (
                  <Col span={24} key={key}>
                    <Row gutter={8} align="middle">
                      <Col span={8} className="text-right font-medium">
                        {tierInfo?.name || `Tier ${name}`}:
                      </Col>
                      <Col span={16}>
                        <Form.Item
                          {...restField}
                          name={[name, 'aprBonusBP']}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            className="w-full"
                            min={0}
                            placeholder="APR Bonus BP"
                            disabled={!isTokenSelected}
                          />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'tierId']} hidden>
                          <InputNumber />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                )
              })}
            </Row>
          )}
        </Form.List>

        <Divider />

        <Row align="middle" style={{ gap: 16 }}>
          <Col>
            <span>Is Active</span>
          </Col>
          <Col>
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={
                isSubmitting || form.getFieldsError().some(({ errors }) => errors.length > 0)
              }
            >
              Submit
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalAddNftCollection
